import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = [
		'map',
		'jsonMapDataScriptTag',
		'overviewTab',
		'stagePointTab',
		'tabButton',
		'dropdownElement',
		'breadcrumbs',
		'stagePointBackButton',
		'marker',
		'stageLink',
	];

	/**
	 * Connect.
	 */
	connect() {
		// Dev Token
		mapboxgl.accessToken = 'pk.eyJ1Ijoic3dlbnN1cGVyaW50ZXJhY3RpdmUiLCJhIjoiY2tpNXJjaDFiNGdoYjJwa3p3em4zb2JqaiJ9.DWtDr87o2w3RwxOoQH_-Ag';

		// Production Token
		// mapboxgl.accessToken = 'pk.eyJ1Ijoic3dlbnN1cGVyaW50ZXJhY3RpdmUiLCJhIjoiY2wzYmZlZGs2MGV4ZjNkbnJ1eHZqcHA5ZyJ9.GpAxUf8xHR5sXioSFjGHaQ';
	}

	/**
	 * Init the MapBox map.
	 */
	initMap() {
		// Load mapsettings
		this.mapSettings = this.getMapSettings();

		if (this.objectLength(this.mapSettings.routes) < 3 && this.objectLength(this.mapSettings.routes) !== 0) {
			const route = this.mapSettings.routes[0];
			// Init mapbox
			this.map = new mapboxgl.Map({
				cooperativeGestures: true,
				container: 'map', // container ID
				style: 'mapbox://styles/swensuperinteractive/cl3bf2is2000t15loa8cb071a', // style URL
				center: [route[0], route[1]],
				zoom: this.mapSettings.zoom,
			});
		} else {
			// Init mapbox
			this.map = new mapboxgl.Map({
				cooperativeGestures: true,
				container: 'map', // container ID
				style: 'mapbox://styles/swensuperinteractive/cl3bf2is2000t15loa8cb071a', // style URL
				center: [5.28800019752873, 52.175784866316846], // center coordinates - start in center of Netherlands
				zoom: 6.5, // starting zoom
			});

			this.zoomMapToRouteBounds(this.getGeoJsonRoute('full'));
		}

		// Add zoom and compass controlls
		this.map.addControl(new mapboxgl.NavigationControl());

		// Add support for fullscreening the map
		this.map.addControl(new mapboxgl.FullscreenControl());

		if (this.objectLength(this.mapSettings.routes) > 2 && this.objectLength(this.mapSettings.routes) === 0) {
			document.addEventListener('fullscreenchange', () => {
				this.map.resize();

				this.zoomMapToRouteBounds(this.getGeoJsonRoute('full'));
			});
		}

		// Add routes to map
		this.map.on('load', () => {
			Object.keys(this.mapSettings.routes).forEach((stageId) => {
				if (stageId == 'full') {
					this.addMapRoute(stageId);
				}
			});

			Object.keys(this.mapSettings.markers).forEach((stageId) => {
				this.addMapMarker(stageId);
			});
		});
	}

	/**
	 * Get GeoJson route.
	 *
	 * @param {Integer} stageId
	 *
	 * @returns JSON
	 */
	getGeoJsonRoute(stageId) {
		return {
			type: 'FeatureCollection',
			features: [
				{
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates: this.mapSettings.routes[stageId],
					},
				},
			],
		};
	}

	/**
	 * Add route to map.
	 *
	 * @param {Integer} stageId
	 */
	addMapRoute(stageId) {
		var lineColor = '#E32517';
		var lineOpacity = 1;
		var lineWidth = 2;

		const geoJsonRoute = this.getGeoJsonRoute(stageId);

		this.map.addSource('stage-' + stageId, {
			type: 'geojson',
			data: geoJsonRoute,
		});

		lineWidth = 4;

		this.map.addLayer({
			id: 'stage-' + stageId,
			type: 'line',
			source: 'stage-' + stageId,
			layout: {
				'line-join': 'round',
				'line-cap': 'round',
			},
			paint: {
				'line-color': lineColor,
				'line-opacity': lineOpacity,
				'line-width': lineWidth,
			},
		});
	}

	/**
	 * Add map marker for given stage.
	 *
	 * @param {Integer} stageId
	 */
	addMapMarker(stageId) {
		const geoJson = this.getGeoJsonMapMarkers(stageId);

		for (const marker of geoJson.features) {
			// Create a DOM element for each marker.
			const markerElement = document.createElement('div');

			markerElement.className = 'trip-marker';
			markerElement.style.backgroundImage = 'url("' + marker.properties.iconUrl + '")';
			markerElement.setAttribute('data-point-id', marker.properties.id);
			markerElement.setAttribute('data-marker-type', marker.properties.type);
			markerElement.setAttribute('data-longitude', marker.geometry.coordinates[0]);
			markerElement.setAttribute('data-latitude', marker.geometry.coordinates[1]);
			markerElement.setAttribute('data-route-target', 'marker');

			if (this.mapSettings.type == 'route') {
				markerElement.style.cursor = 'grab';
			}

			// Add markers to the map.
			new mapboxgl.Marker(markerElement).setLngLat(marker.geometry.coordinates).addTo(this.map);
		}
	}

	/**
	 * Get GeoJson for map markers.
	 *
	 * @param {Integer} stageId
	 *
	 * @returns JSON
	 */
	getGeoJsonMapMarkers(stageId) {
		const geoJsonMapMarkers = {
			type: 'FeatureCollection',
			features: [],
		};

		const currentMarker = this.mapSettings.markers[stageId];

		geoJsonMapMarkers.features.push({
			type: 'Feature',
			properties: {
				id: currentMarker.id,
				iconUrl: currentMarker.iconUrl,
				type: currentMarker.type,
			},
			geometry: {
				type: 'Point',
				coordinates: [currentMarker.coordinates.longitude, currentMarker.coordinates.latitude],
			},
		});

		return geoJsonMapMarkers;
	}

	/**
	 * Zoom map to round bounds.
	 *
	 * @param {JSON} geoJsonRoute
	 */
	zoomMapToRouteBounds(geoJsonRoute, duration = 0) {
		// Geographic coordinates of the LineString
		const coordinates = geoJsonRoute.features[0].geometry.coordinates;

		// Create a 'LngLatBounds' with both corners at the first coordinate.
		const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);

		// Extend the 'LngLatBounds' to include every coordinate in the bounds result.
		for (const coord of coordinates) {
			bounds.extend(coord);
		}

		if (screen.width < 1024) {
			// Set padding on zoomed map
			this.map.fitBounds(bounds, {
				padding: { top: 60, bottom: 120, left: 60, right: 60 },
				duration: duration,
				linear: false,
			});
		} else {
			// Set padding on zoomed map
			this.map.fitBounds(bounds, {
				padding: { top: 60, bottom: 60, left: 550, right: 60 },
				duration: duration,
				linear: false,
			});
		}
	}

	/**
	 * Get map settings (from jsonMapDataScriptTagTarget)
	 *
	 * @returns Object
	 */
	getMapSettings() {
		return JSON.parse(this.jsonMapDataScriptTagTarget.innerHTML);
	}

	objectLength(object) {
		var length = 0;
		for (var key in object) {
			if (object.hasOwnProperty(key)) {
				++length;
			}
		}
		return length;
	}
}
